import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import useStyled from "../../hooks/useStyled";
import SFTypography from "../typography";
import SFDropdownBase from "./base";

const { styled } = useStyled();

export const ListStyled = styled(List)(({ minWidth }) => ({
  zIndex: 10,
  minWidth,
  padding: 0,
}));

const ListItemButtonStyled = styled(ListItemButton)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  "& .MuiListItemIcon-root": {
    minWidth: "32px",
    justifyContent: "center",
    color: theme.palette.grey500.main,
    paddingRight: "8px",
  },
  "& .MuiTypography-root": {
    color: theme.palette.grey500.main,
  },
  "&:hover": {
    backgroundColor: theme.palette.blue100_1.main,
  },
}));

const SFDropdown = ({
  id,
  open,
  anchorEl,
  trigger,
  onClose,
  items = [],
  disableScrollLock = false,
  position = 50,
  placement = "bottom",
  anchorOrigin = null,
  transformOrigin = null,
  fullWidth = false,
  suppressArrow = false,
  minWidth = null,
  htmlPlacement,
}) => {
  const { t } = useTranslation();

  const [listRef, setListRef] = useState(null);

  const list = {
    width: listRef?.offsetWidth,
    height: listRef?.offsetHeight,
  };

  const onClickHandler = (item) => {
    item.onClick();
    onClose();
  };

  if (items.length === 0) {
    items.push({
      element: (
        <ListItem>
          <SFTypography variant="pageActionBar">
            {t("components.dropdown.noOptions")}
          </SFTypography>
        </ListItem>
      ),
    });
  }

  return (
    <SFDropdownBase
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      trigger={trigger}
      position={position}
      placement={!anchorOrigin && !transformOrigin && placement}
      fullWidth={fullWidth}
      suppressArrow={suppressArrow}
      disableScrollLock={disableScrollLock}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      list={list}
      htmlPlacement={htmlPlacement}
    >
      <ListStyled
        {...(minWidth && { minWidth })}
        ref={(newRef) => setListRef(newRef)}
      >
        {items.map((item, index) => {
          if (item.label) {
            return (
              <ListItemButtonStyled
                key={index}
                onClick={onClickHandler.bind(this, item)}
                disabled={item.disabled}
                selected={item.selected}
              >
                {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                <ListItemText>{item.label}</ListItemText>
              </ListItemButtonStyled>
            );
          }

          if (item.element) {
            return <React.Fragment key={index}>{item.element}</React.Fragment>;
          }

          return null;
        })}
      </ListStyled>
    </SFDropdownBase>
  );
};

SFDropdown.propTypes = {
  id: PropTypes.string,
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.object,
  trigger: PropTypes.object,
  onClose: PropTypes.func,
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(["top", "center", "bottom"]),
    horizontal: PropTypes.oneOf(["left", "center", "right"]),
  }),
  transformOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(["top", "center", "bottom"]),
    horizontal: PropTypes.oneOf(["left", "center", "right"]),
  }),
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
        onClick: PropTypes.func,
        disabled: PropTypes.bool,
      }),
      PropTypes.shape({
        element: PropTypes.element,
      }),
    ])
  ).isRequired,
  disableScrollLock: PropTypes.bool,
  position: PropTypes.number,
  placement: PropTypes.oneOf(["top", "bottom", "left", "right"]),
  htmlPlacement: PropTypes.any,
};

export default SFDropdown;
